import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

export default function Team() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "case_manager" });

  const load = () => api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  useEffect(load, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/team", form);
      toast.success(`${form.name} added to the team`);
      setForm({ name: "", email: "", password: "", role: "case_manager" });
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
  };

  return (
    <div className="space-y-5" data-testid="team-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Team</h1>
        <p className="text-sm text-slate-500">Staff accounts with role-based access. Admins manage everything; case managers run daily operations.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-lg border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200">
              {["Name", "Email", "Role", "Joined"].map((h) => (
                <th key={h} className="text-left py-2.5 px-3.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-500">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {team.map((t) => (
                <tr key={t.id} className="border-b border-slate-100">
                  <td className="py-2.5 px-3.5 font-medium text-slate-800">{t.name}</td>
                  <td className="py-2.5 px-3.5 text-slate-600">{t.email}</td>
                  <td className="py-2.5 px-3.5">
                    <Badge variant="outline" className={t.role === "admin" ? "bg-slate-900 text-white border-slate-900" : "bg-sky-50 text-sky-800 border-sky-300"}>
                      {t.role === "admin" ? "Admin" : "Case Manager"}
                    </Badge>
                  </td>
                  <td className="py-2.5 px-3.5 text-slate-500">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {user?.role === "admin" && (
          <form onSubmit={add} className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 h-fit">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><UserPlus className="h-4 w-4" /> Add team member</div>
            <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="team-name-input" /></div>
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="team-email-input" /></div>
            <div><Label>Temporary password</Label><Input required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" data-testid="team-password-input" /></div>
            <div><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="team-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="case_manager">Case Manager</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
            <Button type="submit" data-testid="team-add-btn" className="w-full bg-[#0B2545] hover:bg-[#134074] text-white">Add member</Button>
          </form>
        )}
      </div>
    </div>
  );
}
